import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ACTION_TYPES, useAmendmentCreateEditDispatch, useAmendmentCreateEditState } from './amendment-create-edit-context';
import AmendmentAppService from '../../../../application/platform/sge/amendment/amendment-app-service';
import ParlamentarianAppService from '../../../../application/platform/sge/parlamentarian/parlamentarian-app-service';
import PoliticalPartyAppService from '../../../../application/platform/sge/political-party/political-party-app-service';
import BudgetUnitAppService from '../../../../application/platform/sge/budget-unit/budget-unit-app-service';
import BudgetProgramAppService from '../../../../application/platform/sge/budget-program/budget-program-app-service';
import BudgetActionAppService from '../../../../application/platform/sge/budget-action/budget-action-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { IAmendmentAddEditRequest } from '../../../../model/sge/amendment/request/amendment-add-edit-request';
import { hasErrors } from '../../../../utils/validation';
import { resolveReturnPath, toAmendmentForm, validateAmendment } from '../components/amendment-form-utils';

export const useAmendmentCreateEditController = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAmendmentCreateEditDispatch();
    const state = useAmendmentCreateEditState();
    const { showSuccess, showError, withLoading } = useFeedback();
    const [isSaving, setIsSaving] = useState(false);
    const { amendmentId, selectedBudgetUnitId, selectedBudgetProgramId } = state;

    useEffect(() => {
        async function loadLookups() {
            const [parlamentarians, politicalParties, budgetUnits] = await Promise.all([
                ParlamentarianAppService.list({ page: 0, pageSize: 100 }),
                PoliticalPartyAppService.listAll(),
                BudgetUnitAppService.list(),
            ]);
            dispatch({ type: ACTION_TYPES.setParlamentarians, payload: parlamentarians.items });
            dispatch({ type: ACTION_TYPES.setPoliticalParties, payload: politicalParties });
            dispatch({ type: ACTION_TYPES.setBudgetUnits, payload: budgetUnits });
        }

        // Na edição, unidade e programa são deduzidos da ação orçamentária gravada
        async function loadAmendment(id: number) {
            const data = await AmendmentAppService.getById(id);
            dispatch({ type: ACTION_TYPES.setAmendmentRequest, payload: toAmendmentForm(data) });
            if (!data.budgetActionId) return;
            const action = await BudgetActionAppService.getById(data.budgetActionId);
            const program = await BudgetProgramAppService.getById(action.budgetProgramId);
            dispatch({ type: ACTION_TYPES.setSelectedBudgetUnitId, payload: program.budgetUnitId });
            dispatch({ type: ACTION_TYPES.setSelectedBudgetProgramId, payload: action.budgetProgramId });
        }

        withLoading(async () => {
            await loadLookups();
            if (amendmentId) await loadAmendment(Number(amendmentId));
        }).catch((error: any) => showError('Não foi possível carregar os dados da emenda', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amendmentId]);

    // Cascata unidade → programa → ação
    useEffect(() => {
        if (!selectedBudgetUnitId) {
            dispatch({ type: ACTION_TYPES.setBudgetPrograms, payload: [] });
            return;
        }
        BudgetProgramAppService.list({ budgetUnitId: selectedBudgetUnitId })
            .then((programs) => dispatch({ type: ACTION_TYPES.setBudgetPrograms, payload: programs }))
            .catch((error: any) => showError('Não foi possível carregar os programas orçamentários', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBudgetUnitId]);

    useEffect(() => {
        if (!selectedBudgetProgramId) {
            dispatch({ type: ACTION_TYPES.setBudgetActions, payload: [] });
            return;
        }
        BudgetActionAppService.list({ budgetProgramId: selectedBudgetProgramId })
            .then((actions) => dispatch({ type: ACTION_TYPES.setBudgetActions, payload: actions }))
            .catch((error: any) => showError('Não foi possível carregar as ações orçamentárias', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBudgetProgramId]);

    function updateRequest(patch: Partial<IAmendmentAddEditRequest>) {
        dispatch({ type: ACTION_TYPES.updateAmendmentRequest, payload: patch });
    }

    // O partido acompanha o parlamentar escolhido (pode ser alterado depois)
    function handleParlamentarianChange(parlamentarId?: number) {
        const parlamentarian = state.parlamentarians.find((p) => p.id === parlamentarId);
        updateRequest({ parlamentarId, politicalPartyId: parlamentarian?.politicalPartyId });
    }

    function handleBudgetUnitChange(budgetUnitId?: number) {
        dispatch({ type: ACTION_TYPES.setSelectedBudgetUnitId, payload: budgetUnitId });
        dispatch({ type: ACTION_TYPES.setSelectedBudgetProgramId, payload: undefined });
        updateRequest({ budgetActionId: undefined });
    }

    function handleBudgetProgramChange(budgetProgramId?: number) {
        dispatch({ type: ACTION_TYPES.setSelectedBudgetProgramId, payload: budgetProgramId });
        updateRequest({ budgetActionId: undefined });
    }

    function handleFilesChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) return;
        dispatch({ type: ACTION_TYPES.setFiles, payload: Array.from(e.target.files) });
    }

    async function handleSave() {
        const errors = validateAmendment(state.amendmentRequest, { budgetUnitId: selectedBudgetUnitId, budgetProgramId: selectedBudgetProgramId });
        if (hasErrors(errors)) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            return;
        }
        const payload = { ...state.amendmentRequest, files: state.files.length > 0 ? state.files : undefined };
        setIsSaving(true);
        try {
            if (amendmentId) {
                await AmendmentAppService.edit(Number(amendmentId), payload);
                showSuccess('Emenda atualizada com sucesso!');
                navigate(`/emendas/${amendmentId}`);
            } else {
                const created = await AmendmentAppService.add(payload);
                showSuccess('Emenda adicionada com sucesso!');
                navigate(`/emendas/${created.id}`);
            }
        } catch (error: any) {
            showError('Não foi possível salvar a emenda', error);
        } finally {
            setIsSaving(false);
        }
    }

    function handleCancel() {
        navigate(resolveReturnPath(location.state));
    }

    return {
        isSaving, updateRequest, handleParlamentarianChange, handleBudgetUnitChange, handleBudgetProgramChange,
        handleFilesChange, handleSave, handleCancel,
    };
};
